import { computedStruct, makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import type { ICoachGateway } from '@/domain/coachGateway';
import { DomainError, OfflineError, ServerError, UpdateRequiredError } from '@/domain/errors';
import {
  playerAttributes,
  sessionStatus,
  type Attribute,
  type Court,
  type PlayerNumber,
  type SessionEvent,
} from '@/domain/session';
import { UuidV7Gen, type IIdGen } from '@/services/IdGen';
import type { ILogger } from '@/services/Logger';

// The coach's whole screen: one court's players, ordered by number, four buttons a row
// (docs/scoring.md, "The coach interface"). Pinned by session/contracts/p3-voting.md and by the
// blind oracle beside this file, which the implementation may not edit.

// One row of the coach list. Four fields, and the contract enumerates them: the coach reads the
// score to know who is doing well, and the two flags are the buttons' current state.
// Which of a row's four buttons a tap belongs to. The view needs this to ask whether THAT button
// is mid-flight, so it is part of the store's surface rather than an internal detail.
export type CoachAction = 'up' | 'down' | 'big' | 'lightbulb';

// One in-flight slot per button per player. Deliberately not per player and not per store: a coach
// waiting on player 10's upvote must still be able to vote player 50, and must still be able to
// mark player 10 BIG.
function pendingKey(player: PlayerNumber, action: CoachAction): string {
  return `${action}:${player}`;
}

export interface CoachRow {
  readonly number: PlayerNumber;
  readonly score: number;
  readonly big: boolean;
  readonly lightbulb: boolean;
}

export class CoachStore {
  // The last tap that reached the gateway and failed. Held until the next tap that LANDS, because
  // a coach who did not see the failure would carry on believing the vote counted (docs/sync.md).
  private _failedMessage: string | undefined;

  // Which tap the message belongs to. Taps overlap: a coach's thumb does not wait for an ack, and
  // the SDK throws OfflineError at ENTRY, before the write mutex, so a tap taken while a slow
  // write is still inside it settles FIRST. Without a sequence number the slow ack would clear a
  // newer tap's failure and leave a dropped vote with nothing on the screen to say so. `_issued`
  // counts the taps that reached the gateway (a gated tap is not one), `_failedSeq` is the tap the
  // message on screen belongs to. Neither is observable: they order the writes, they are not read
  // by the view.
  private _issued = 0;
  private _failedSeq: number | undefined;

  // Buttons with a write still in the air. Observable, because the view disables and marks each
  // one from it.
  private readonly _pending = new Set<string>();

  // The court comes from the redeemed code at construction (docs/access.md): no switcher, no
  // setter, no picker anywhere on this screen.
  constructor(
    readonly court: Court,
    private readonly gateway: ICoachGateway,
    private readonly logger: ILogger,
    // Optional, and last, so the three-argument construction the contract pins still compiles.
    // The store sources its own keys: one tap is one vote, so nothing above it holds a key across
    // calls the way the front desk's OCC ladder does. It defaults to the same generator di.ts
    // registers rather than to a hand-rolled string, because the SDK's `execute` throws at the
    // wire boundary on an idempotency key that is not a canonical hyphenated UUID
    // (session/scraps.md, the P2 SDK note).
    private readonly idGen: IIdGen = new UuidV7Gen(),
  ) {
    // `court` never changes, so nothing derives a reaction from it and it stays out of the graph.
    //
    // `rows` is compared STRUCTURALLY, and that is the one performance decision on this screen.
    // The fold is a single observable ref, so every event in the hall invalidates this computed:
    // four courts means three quarters of the votes landing here are on players this coach cannot
    // see, and a plain computed would return a new array for each one and re-render all 25 rows.
    // With a structural comparison the recomputation still runs (~2us at 100 players) and the
    // observer is only notified when a row this coach can actually see has moved. Measured, not
    // assumed: session/scraps.md carries the numbers and what else was tried.
    makeAutoObservable<CoachStore, '_issued' | '_failedSeq'>(this, {
      court: false,
      _issued: false,
      _failedSeq: false,
      rows: computedStruct,
    });
  }

  // This court's active players, ascending by number. NEVER by score: a list that re-sorts as
  // votes land moves rows under a coach's thumb and turns every burst of voting into mis-taps
  // (docs/scoring.md). `players` is already in number order, so the filter alone preserves it —
  // no sort, and therefore no chance of one creeping in on the score.
  get rows(): readonly CoachRow[] {
    const rows: CoachRow[] = [];
    for (const player of this.gateway.sessionState.players) {
      // Struck-off players vanish from the list entirely (docs/roster.md), and a coach never sees
      // another court.
      if (player.struckOff || player.court !== this.court) continue;
      rows.push({
        number: player.number,
        score: player.score,
        big: player.big,
        lightbulb: player.lightbulb,
      });
    }
    return rows;
  }

  // Running AND connected. The buttons gate on this; the gateway's rejection is the backstop, not
  // the UX (docs/sync.md, "Failure handling").
  get canVote(): boolean {
    return sessionStatus(this.gateway.sessionState) === 'running' && this.gateway.connectionOk;
  }

  // Why the buttons are dead, when they are. Undefined while voting is open. Derived, not set by a
  // tap: a coach should be able to see why the screen is closed without pressing something first.
  get notice(): string | undefined {
    // The connection first, and deliberately. An offline device is reading a fold that stopped
    // arriving, so its idea of the session status is the one thing on this screen that may already
    // be wrong; "offline" is true whatever the session is doing, and it is the half a human in the
    // hall can act on.
    if (!this.gateway.connectionOk) {
      return 'Offline. Votes cannot reach the session until this device is back on the wifi.';
    }
    const status = sessionStatus(this.gateway.sessionState);
    if (status === 'scheduled') {
      return 'The session has not started yet. Voting opens when the admin starts it.';
    }
    // Not "finished": a stop is a pause. `sessionStarted`'s precondition is `status !== 'running'`,
    // so the admin can start a stopped session again and voting reopens (docs/session-lifecycle.md,
    // "Stop is reversible ... There is no separate pause button, because stop already is one").
    if (status === 'stopped') {
      return 'The session is stopped. Voting is closed, and opens again if the admin starts it.';
    }
    return undefined;
  }

  // A tap that reached the gateway and failed. A silently dropped vote is worse than a dropped
  // one (docs/sync.md), so this is visible until the next tap that lands.
  get failedMessage(): string | undefined {
    return this._failedMessage;
  }

  // Is THIS button waiting on the gateway? The view disables it and shows it working, which is
  // the whole point: a coach with a whistle in the other hand cannot tell a slow write from an
  // ignored tap, so they tap again, and again.
  isPending(player: PlayerNumber, action: CoachAction): boolean {
    return this._pending.has(pendingKey(player, action));
  }

  // The four taps. None of them ever rejects: a coach's thumb is not an error boundary, and every
  // call site is `void store.upvote(n)`, where a rejection would reach no catch and no boundary
  // (INVARIANTS rule 17).
  upvote = async (player: PlayerNumber): Promise<void> => {
    await this.write({ kind: 'voteCast', payload: { player, direction: 'up' } }, player, 'up');
  };

  downvote = async (player: PlayerNumber): Promise<void> => {
    await this.write({ kind: 'voteCast', payload: { player, direction: 'down' } }, player, 'down');
  };

  toggleBig = async (player: PlayerNumber): Promise<void> => {
    await this.toggle(player, 'big');
  };

  toggleLightbulb = async (player: PlayerNumber): Promise<void> => {
    await this.toggle(player, 'lightbulb');
  };

  // The flag's current value comes out of the FOLD on every tap, never out of a memory of what
  // this device last sent: another coach on this court may have cleared it in between, and a store
  // flipping its own last value would send `true` again and stick the flag on. The event then
  // carries the resulting value rather than a flip instruction, so two coaches tapping in the same
  // moment do not cancel into a no-op (docs/scoring.md).
  private toggle = async (player: PlayerNumber, attribute: Attribute): Promise<void> => {
    const current = playerAttributes(this.gateway.sessionState, player)[attribute];
    await this.write(
      { kind: 'attributeSet', payload: { player, attribute, value: !current } },
      player,
      attribute === 'big' ? 'big' : 'lightbulb',
    );
  };

  // ONE execute per tap and no ladder. `occ: false` cannot conflict, so there is nothing to climb,
  // and sync.md forbids queues and replays: a vote that failed is gone and the coach is told.
  private write = async (
    event: SessionEvent,
    player: PlayerNumber,
    action: CoachAction,
  ): Promise<void> => {
    if (!this.canVote) {
      // No gateway call, nothing thrown, and `notice` already says why. A dead button that also
      // logged an error every time a distracted coach leaned on it would bury the real failures.
      this.logger.debug(`coach ${this.court}: ${event.kind} refused, voting is closed`);
      return;
    }
    // A second tap on a button whose write is still in the air is a thumb bouncing, not a second
    // vote. DROPPED, not queued: sync.md forbids queues, and the SDK serialises execute() per
    // aggregate behind a lock, so a smashed button does not go faster, it just buries the coach's
    // real taps behind a line of stale ones that all resolve minutes later.
    //
    // Repeat voting is still legitimate and still works (docs/scoring.md: votes accumulate, and
    // two taps must count twice). The guard costs one round trip between deliberate votes, and
    // the button says so while it waits rather than looking dead.
    const key = pendingKey(player, action);
    if (this._pending.has(key)) {
      this.logger.debug(`coach ${this.court}: ${event.kind} ignored, that button is mid-flight`);
      return;
    }
    this._pending.add(key);
    // Taken BEFORE the await, so the number orders the taps by when they LEFT rather than by when
    // they came back.
    const seq = ++this._issued;
    try {
      // A fresh key per tap. Two taps inside the SDK's 90-second dedup window sharing a key is a
      // coach pressing twice and being counted once (docs/sync.md).
      await this.gateway.execute(event, { idempotencyKey: this.idGen.next() });
      // Nothing optimistic here on purpose: the ack is not the fold. A non-OCC write resolves
      // before its event reaches state, so the score moves when the next poke arrives and not a
      // moment earlier. A row bumped here would show this coach a number no other device holds.
      this.clearFailure(seq);
    } catch (cause) {
      // Translate first: the gateway is a boundary over foreign code, and a bug inside it arrives
      // as a TypeError that must not be absorbed as if it were a gateway outage (rule 17).
      const failure =
        cause instanceof DomainError ? cause : new ServerError('The vote failed.', { cause });
      this.logger.warn(`coach ${this.court}: ${event.kind} failed`, failure.tag);
      this.showFailure(voteMessage(failure), seq);
    } finally {
      // Released on failure too. A button that stayed dead after a failed vote would contradict
      // the message beside it, which says "tap again".
      this._pending.delete(key);
    }
  };

  // Actions. makeAutoObservable has wrapped every method and the async ones are flows, so nothing
  // here needs runInAction (INVARIANTS rule 14).

  // Newest failure wins. An older tap coming back late must not paint over the failure of a tap
  // that left after it, because the message says "tap again" and the coach's next move depends on
  // which vote it is about.
  private showFailure(message: string, seq: number): void {
    if (this._failedSeq !== undefined && seq < this._failedSeq) return;
    this._failedMessage = message;
    this._failedSeq = seq;
  }

  // A tap that LANDS clears the banner, and only the banner of a tap that left before it. The
  // slow-ack case is the whole reason: tap A leaves, tap B leaves, B is rejected, A is acked —
  // and B's vote is gone, so B's message stays on the screen (docs/sync.md, a silently dropped
  // vote is worse than a dropped one).
  private clearFailure(seq: number): void {
    if (this._failedSeq !== undefined && this._failedSeq > seq) return;
    this._failedMessage = undefined;
    this._failedSeq = undefined;
  }
}

// What the coach is told when a tap did not land. Every message says the vote was NOT counted,
// because the coach's next move is deciding whether to tap again, and it never says "try again"
// where trying again cannot work.
function voteMessage(failure: DomainError): string {
  if (failure instanceof OfflineError) return 'Offline. That vote was not counted.';
  // Not "tap again": every tap on this build fails the same way until the page is reloaded onto a
  // newer one, and a coach told to tap again would stand there tapping.
  if (failure instanceof UpdateRequiredError) {
    return 'This app is out of date. That vote was not counted — reload the page.';
  }
  return 'That vote did not reach the session. It was not counted — tap again.';
}

// One coach store per court. In production a device holds exactly one, for the court its code
// granted; the type is a function so that a screen never has to know how that instance is kept
// alive (INVARIANTS rule 5). di.ts owns the lifetime.
export type CoachStoreFor = (court: Court) => CoachStore;

export const TOKEN_CoachStoreFor = token<CoachStoreFor>('CoachStoreFor');
